import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, Dimensions } from 'react-native';
import { colores } from '../../../../styles/variablesCss';
import Combinables from './Combinables';
import PluDeRegalo from './PluDeRegalo';
import ComboPrecioFijo from './ComboPrecioFijo';

/**
 * Component for displaying different types of promotion banners on product cards
 * @param {Object} props - Component props
 * @param {Object|null} combo - Combo information if available
 * @param {string|null} oferta - Offer type code if available
 * @param {function} onNavigateToDetail - Function to navigate to product detail
 * @param {Object} producto - Información completa del producto
 */
const BanderaPromo = ({ combo, oferta, onNavigateToDetail, producto,}) => {
  // Función de sanitización para evitar renderizar valores inválidos
  const safeText = (value, defaultValue = '') => {
    if (!value || value === '.' || value === null || value === undefined) return defaultValue;
    const stringValue = String(value).trim();
    if (stringValue === '' || stringValue === '.') return defaultValue;
    return stringValue;
  };

  // Estado para controlar la visibilidad del modal de Combinables
  const [modalVisible, setModalVisible] = useState(false);
  // Estado para controlar la visibilidad del modal de Plu de Regalo
  const [modalRegaloVisible, setModalRegaloVisible] = useState(false);
  // Estado para controlar la visibilidad del modal de Combo Precio Fijo
  const [modalComboFijoVisible, setModalComboFijoVisible] = useState(false);

  // Responsive: detectar tamaño de pantalla con breakpoints granulares
  const initialWidth = Platform.OS === 'web' && typeof window !== 'undefined'
    ? window.innerWidth
    : Dimensions.get('window').width;
  const [viewportWidth, setViewportWidth] = useState(initialWidth);
  
  // Breakpoints responsivos
  const isExtraSmall = viewportWidth < 400;  // Móviles pequeños
  const isSmallScreen = viewportWidth < 600; // Móviles
  const isTablet = viewportWidth >= 600 && viewportWidth < 992; // Tablets
  const isDesktop = viewportWidth >= 992;    // Desktop
  
  // Calcular ancho del banner según tamaño de pantalla
  const getBannerWidth = () => {
    if (isExtraSmall) return '85%';
    if (isSmallScreen) return '75%';
    if (isTablet) return '55%';
    return '45%';
  };
  // Calcular tamaño de fuente según pantalla
  const getFontSize = () => {
    if (isExtraSmall) return { title: 11, button: 10 };
    if (isSmallScreen) return { title: 12, button: 11 };
    if (isTablet) return { title: 13, button: 11 };
    return { title: 14, button: 12 };
  };
  
  const bannerWidth = getBannerWidth();
  const fontSizes = getFontSize();

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onResize = () => setViewportWidth(window.innerWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);
  // Show combo banner if combo info is available
  if (combo && combo.length > 0) {
    // Verificar si el combo es combinable
    const isCombinable = combo[0].tipoCombo === 2;
    // Verificar si el combo tiene producto de regalo
    const tienePluRegalo = combo[0].tipoCombo === 3;
    // Verificar si el combo es un combo normal
    const comboPrecioFijo = combo[0].tipoCombo === 4;
     // Verificar si el combo es un combo normal
    const comboPrecioFijoMasDeDos = combo[0].tipoCombo === 5;
    // Suma de cantOblig para determinar si mostrar solo precio
    const totalCantOblig = combo.reduce((sum, c) => sum + (c.cantOblig || 0), 0);
    const hasCantOblig = !!combo[0].cantOblig;
    const shouldShowComboButton = (comboPrecioFijo || hasCantOblig) && !tienePluRegalo && comboPrecioFijoMasDeDos;
    
    // Función para extraer solo el precio del descom (ej: "2 x $18990" -> "$18990")
    const getPrecioFromDescom = () => {
      if (!combo[0]?.descom) return null;
      const descomSafe = safeText(combo[0].descom);
      if (!descomSafe) return null;
      const match = descomSafe.match(/\$[\d.,]+/);
      return match ? match[0] : descomSafe;
    };
    
    return (
      <View style={[styles.comboContainer, { width: bannerWidth }]}>
        <View style={[styles.comboTitleContainer, isExtraSmall && { paddingVertical: 4 }]}>
          <Text style={[styles.comboTitleText, { fontSize: fontSizes.title }]}>
            {totalCantOblig > 0 ? (getPrecioFromDescom() || safeText(combo[0]?.descom, 'COMBO')) : safeText(combo[0]?.descom, 'COMBO')}
          </Text>
        </View>
        
        {/* Botón P. REGALO para productos con combo de regalo */}
        {tienePluRegalo && (
          <>
            <TouchableOpacity
              style={[styles.combinalosButton, isExtraSmall && { paddingVertical: 3, paddingHorizontal: 6 }]}
              onPress={() => setModalRegaloVisible(true)}
            >
              <Text style={[styles.combinalosText, { fontSize: fontSizes.button }]}>VER REGALO</Text>
            </TouchableOpacity>
            
            {/* Modal de Plu de Regalo */}
            <PluDeRegalo 
              visible={modalRegaloVisible}
              onClose={() => setModalRegaloVisible(false)}
              combo={combo[0]}
              producto={producto}
            />
          </>
        )}
        
        {/* Botón VER COMBO para combos de precio fijo o con cantOblig */}
        {shouldShowComboButton && (
          <>
            <TouchableOpacity
              style={[styles.combinalosButton, isExtraSmall && { paddingVertical: 3, paddingHorizontal: 6 }]}
              onPress={() => setModalComboFijoVisible(true)}
            >
              <Text style={[styles.combinalosText, { fontSize: fontSizes.button }]}>VER COMBO</Text>
            </TouchableOpacity>

            {/* Modal de Combo Precio Fijo */}
            <ComboPrecioFijo
              visible={modalComboFijoVisible}
              onClose={() => setModalComboFijoVisible(false)}
              combo={combo[0]}
              producto={producto}
            />
          </>
        )}

        {/* Botón Combinalos para productos con combos combinables */}
        {!tienePluRegalo && isCombinable &&  (
          <>
            <TouchableOpacity
              style={[styles.combinalosButton, isExtraSmall && { paddingVertical: 3, paddingHorizontal: 6 }]}
              onPress={() => setModalVisible(true)}
            >
              <Text style={[styles.combinalosText, { fontSize: fontSizes.button }]}>ARMAR COMBO</Text>
            </TouchableOpacity>
            
            {/* Modal de Combinables */}
            <Combinables 
              visible={modalVisible}
              onClose={() => setModalVisible(false)}
              combo={combo[0]}
              producto={producto}
            />
          </>
        )}
        
      </View>
    );
  }
  
  // Show offer banner for normal offers
  if (oferta === 'N') {
    return (
      <View style={[styles.ofertaTitleContainer, { width: bannerWidth }, isExtraSmall && { paddingVertical: 4 }]}>
        <Text style={[styles.ofertaTitleText, { fontSize: fontSizes.title }]}>OFERTA</Text>
      </View>
    );
  }

  // Show special combo banner for combo offers
  if (oferta === 'D' && combo && Array.isArray(combo) && combo.length > 0 && combo[0]) {
    return (
      <View style={[styles.comboOfertaContainer, { width: bannerWidth }, isExtraSmall && { paddingVertical: 4 }]}>
        <Text style={[styles.comboOfertaText, { fontSize: fontSizes.title }]}>COMBO</Text>
      </View>
    );
  }

  // Show offer banner for normal offers
  if (oferta === 'D') {
    return (
      <View style={[styles.ofertaTitleContainer, { width: bannerWidth }, isExtraSmall && { paddingVertical: 4 }]}>
        <Text style={[styles.ofertaTitleText, { fontSize: fontSizes.title }]}>OFERTA</Text>
      </View>
    );
  }
  
  // No promotion banner to display
  return null;
};

const styles = StyleSheet.create({
  comboContainer: {
    position: 'absolute',
    right: 0,
    top: 25,
    zIndex: 2,
    width: '45%',
    alignItems: 'center',
  },
  comboTitleContainer: {
    width: '100%',
    backgroundColor: colores.negro,
    paddingVertical: 6,
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    zIndex: 2,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.5,
        shadowRadius: 3,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
      },
    }),
  },
  comboTitleText: {
    color: colores.blanco,
    fontWeight: 'bold',
    fontSize: 14,
    textAlign: 'center',
  },
  combinalosButton: {
    backgroundColor: colores.rojo,
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 10,
    marginTop: 5,
    width: '90%',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.2,
          shadowRadius: 2,
          elevation: 2,
        }
    ),
  },
  combinalosText: {
    color: colores.blanco,
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'center',
  },
  
  ofertaTitleContainer: {
    position: 'absolute',
    top: 25,
    right: 0,
    width: '45%',
    backgroundColor: colores.negro,
    paddingVertical: 6,
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    zIndex: 2,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.5,
        shadowRadius: 3,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
      },
    }),
  },
  ofertaTitleText: {
    color: colores.blanco,
    fontWeight: 'bold',
    fontSize: 14,
    textAlign: 'center',
  },
  comboOfertaContainer: {
    position: 'absolute',
    top: 25,
    right: 0,
    width: '45%',
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    backgroundColor: 'black',
    paddingVertical: 6,
    zIndex: 2,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.5,
        shadowRadius: 3,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
      },
    }),
  },
  comboOfertaText: {
    color: colores.blanco,
    fontWeight: 'bold',
    fontSize: 14,
    textAlign: 'center',
  },
});

export default BanderaPromo;
